import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { inventoryAPI, bookingAPI } from '../services/api';

const BookingCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialItem = location.state?.selectedItem;
  const initialStartDate = location.state?.startDate || new Date();
  const initialEndDate = location.state?.endDate || new Date(new Date().getTime() + 24 * 60 * 60 * 1000);

  const [formData, setFormData] = useState({
    pickupDatetime: initialStartDate,
    returnDatetime: initialEndDate,
    purpose: '',
    additionalNotes: '',
    alternativePickupPerson: '',
    inventoryItems: initialItem 
      ? [{ inventoryItemId: initialItem.id, quantity: 1 }] 
      : []
  });

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemDetails, setItemDetails] = useState({});
  const [alternativePickup, setAlternativePickup] = useState(false);

  const fetchAvailableItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const response = await inventoryAPI.getAvailableItems(
        format(formData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        format(formData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss")
      );
      setAvailableItems(response.data);
    } catch (err) {
      console.error('Failed to fetch available items', err);
      setError('Failed to load available items. Please try again.');
    } finally {
      setItemsLoading(false);
    }
  }, [formData.pickupDatetime, formData.returnDatetime]);

  useEffect(() => {
    fetchAvailableItems();
  }, [fetchAvailableItems]);

  useEffect(() => {
    // Fetch details for items in the booking
    const fetchItemDetails = async () => {
      const details = {};
      for (const item of formData.inventoryItems) {
        if (!itemDetails[item.inventoryItemId]) {
          try {
            const response = await inventoryAPI.getItemById(item.inventoryItemId);
            details[item.inventoryItemId] = response.data;
          } catch (err) {
            console.error(`Failed to fetch details for item ${item.inventoryItemId}`, err);
          }
        }
      }
      setItemDetails(prev => ({ ...prev, ...details }));
    };

    fetchItemDetails();
  }, [formData.inventoryItems, itemDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAlternativePickupChange = (e) => {
    setAlternativePickup(e.target.checked);
    if (!e.target.checked) {
      // Clear the alternativePickupPerson field when unchecked
      setFormData(prev => ({
        ...prev,
        alternativePickupPerson: ''
      }));
    }
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to calculate effective available quantity
  const getEffectiveAvailableQuantity = (itemId) => {
    const availableItem = availableItems.find(ai => ai.id === parseInt(itemId));
    if (!availableItem) return 0;

    // Calculate how many are already in the current booking
    const currentlyBooked = formData.inventoryItems.reduce((total, i) => {
      if (i.inventoryItemId === parseInt(itemId)) {
        return total + i.quantity;
      }
      return total;
    }, 0);

    return availableItem.availableQuantity - currentlyBooked;
  };

  const handleAddItem = () => {
    if (!selectedItemId || selectedQuantity < 1) return;

    const itemId = parseInt(selectedItemId);
    const quantity = parseInt(selectedQuantity);

    // Check available quantity considering items already in the booking
    const effectiveAvailableQuantity = getEffectiveAvailableQuantity(itemId);

    if (quantity > effectiveAvailableQuantity) {
      setError(`Неможливо додати ${quantity} одиниць. Доступно лише ${effectiveAvailableQuantity}.`);
      return;
    }

    // Check if item is already in the list
    const existingItemIndex = formData.inventoryItems.findIndex(
      item => item.inventoryItemId === itemId
    );

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      const updatedItems = [...formData.inventoryItems];
      updatedItems[existingItemIndex].quantity += quantity;

      setFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    } else {
      // Add new item
      setFormData(prev => ({
        ...prev,
        inventoryItems: [
          ...prev.inventoryItems,
          {
            inventoryItemId: itemId,
            quantity: quantity
          }
        ]
      }));
    }

    // Reset selection and error
    setSelectedItemId('');
    setSelectedQuantity(1);
    setError('');
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...formData.inventoryItems];
    updatedItems.splice(index, 1);

    setFormData(prev => ({
      ...prev,
      inventoryItems: updatedItems
    }));
  };

  const handleIncreaseQuantity = (index) => {
    const updatedItems = [...formData.inventoryItems];
    const item = updatedItems[index];
    const inventoryItemId = item.inventoryItemId;

    // Use the helper function to get effective available quantity
    const effectiveAvailableQuantity = getEffectiveAvailableQuantity(inventoryItemId);

    // Only increase if there's still available quantity
    if (effectiveAvailableQuantity > 0) {
      updatedItems[index].quantity += 1;

      setFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    } else {
      setError(`Неможливо додати більше одиниць цього предмету. Досягнуто максимальну доступну кількість.`);
    }
  };

  const handleDecreaseQuantity = (index) => {
    const updatedItems = [...formData.inventoryItems];

    // Only decrease if quantity is greater than 1
    if (updatedItems[index].quantity > 1) {
      updatedItems[index].quantity -= 1;

      setFormData(prev => ({
        ...prev,
        inventoryItems: updatedItems
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.inventoryItems.length === 0) {
      setError('Будь ласка, додайте хоча б один предмет до вашого бронювання.');
      return;
    }

    if (!formData.purpose) {
      setError('Будь ласка, вкажіть мету вашого бронювання.');
      return;
    }

    if (formData.pickupDatetime >= formData.returnDatetime) {
      setError('Час отримання має бути раніше часу повернення.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await bookingAPI.createBooking({
        ...formData,
        pickupDatetime: format(formData.pickupDatetime, "yyyy-MM-dd'T'HH:mm:ss"),
        returnDatetime: format(formData.returnDatetime, "yyyy-MM-dd'T'HH:mm:ss")
      });

      navigate('/bookings', { state: { message: 'Бронювання успішно створено!' } });
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Не вдалося створити бронювання. Будь ласка, перевірте вашу інформацію та спробуйте знову.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Створення нового бронювання
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Дата і час отримання"
                  value={formData.pickupDatetime}
                  onChange={(newValue) => handleDateChange('pickupDatetime', newValue)}
                  sx={{ width: '100%' }}
                  format="dd.MM.yyyy HH:mm"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Дата і час повернення"
                  value={formData.returnDatetime}
                  onChange={(newValue) => handleDateChange('returnDatetime', newValue)}
                  sx={{ width: '100%' }}
                  format="dd.MM.yyyy HH:mm"
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          <TextField
            margin="normal"
            required
            fullWidth
            id="purpose"
            label="Мета бронювання"
            name="purpose"
            value={formData.purpose}
            onChange={handleInputChange}
          />

          <TextField
            margin="normal"
            fullWidth
            id="additionalNotes"
            label="Додаткові примітки"
            name="additionalNotes"
            multiline
            rows={3}
            value={formData.additionalNotes}
            onChange={handleInputChange}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={alternativePickup}
                onChange={handleAlternativePickupChange}
                color="primary"
              />
            }
            label="Хтось інший забере предмети"
          />

          <Collapse in={alternativePickup} timeout="auto" unmountOnExit>
            <TextField
              margin="normal"
              fullWidth
              id="alternativePickupPerson"
              label="Особа, яка забере предмети"
              name="alternativePickupPerson"
              value={formData.alternativePickupPerson}
              onChange={handleInputChange}
              helperText="Введіть повне ім'я особи, яка забере предмети"
            />
          </Collapse>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Вибрані предмети
          </Typography>

          <List>
            {formData.inventoryItems.map((item, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={itemDetails[item.inventoryItemId]?.name || `Item #${item.inventoryItemId}`}
                  secondary={`Кількість: ${item.quantity}`}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handleDecreaseQuantity(index)}>
                    <RemoveIcon />
                  </IconButton>
                  <IconButton onClick={() => handleIncreaseQuantity(index)}>
                    <AddIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleRemoveItem(index)}>
                    <DeleteIcon color="primary" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {formData.inventoryItems.length === 0 && (
              <ListItem>
                <ListItemText primary="Не вибрано жодного предмета" />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Додати предмети
          </Typography>

          {itemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Виберіть предмет"
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value=""></option>
                  {availableItems.map((item) => {
                    // Calculate effective available quantity for display
                    const effectiveAvailable = getEffectiveAvailableQuantity(item.id);
                    return (
                      <option 
                        key={item.id} 
                        value={item.id}
                        disabled={effectiveAvailable <= 0}
                      >
                        {item.name} (Доступно: {effectiveAvailable})
                      </option>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Кількість"
                  type="number"
                  InputProps={{ 
                    inputProps: { 
                      min: 1,
                      max: selectedItemId ? getEffectiveAvailableQuantity(selectedItemId) : 1
                    } 
                  }}
                  value={selectedQuantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    const max = selectedItemId ? getEffectiveAvailableQuantity(selectedItemId) : 1;
                    if (value > max) {
                      setSelectedQuantity(max);
                      setError(`Максимальна доступна кількість: ${max}`);
                    } else {
                      setSelectedQuantity(value);
                      setError('');
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  disabled={!selectedItemId}
                  fullWidthy
                >
                  Додати
                </Button>
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Скасувати
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || formData.inventoryItems.length === 0}
            >
              {loading ? 'Створення...' : 'Створити бронювання'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BookingCreate;
